// Copyright ©️ 2026 eVolve MEP, LLC

import type { StrongOmit } from '@evolvemep/api-ts';
import { Stack, getDefaultZIndex } from '@mantine/core';
import type { CustomFilterProps } from 'ag-grid-react';

import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { isNil } from 'helpers/isNotNil';

import type { HandledFilterModel } from './convertAgGridRequest';

type Props<T extends string> = {
  label: string;
  placeholder?: string;
  data: (
    | T
    | {
        value: T;
        label: string;
      }
  )[];
  defaultValue?: T | null;
  filterKey?: string;
  /**
   * If true, will treat an empty string as nil
   *
   * @defaultValue true
   */
  treatEmptyAsNil?: boolean;
};

type FilterProps<T extends string> = StrongOmit<
  // oxlint-disable-next-line typescript/no-explicit-any
  CustomFilterProps<any, T>,
  'model' | 'onModelChange'
> & {
  model: (HandledFilterModel<T> & { filterType: 'custom' }) | undefined;
  onModelChange: (newModel: FilterProps<T>['model'] | null) => void;
  buttons?: ['reset'];
};

export const CustomSelectComponent = <T extends string>({
  label,
  defaultValue = null,
  data,
  filterKey,
  placeholder = 'Select...',
  treatEmptyAsNil = true,
}: Props<T>) =>
  function ({ model, onModelChange, api, buttons = ['reset'] }: FilterProps<T>) {
    return (
      <Stack align="flex-end" p="sm">
        <WrappedSelect<T>
          value={model?.value ?? defaultValue}
          defaultValue={defaultValue}
          label={label}
          placeholder={placeholder}
          data={data.map((d) =>
            typeof d === 'string'
              ? {
                  value: d,
                  label: d,
                }
              : d,
          )}
          classNames={{
            // Necessary for AG Grid to not close the filter
            // when we click on the popover
            dropdown: 'ag-custom-component-popup',
          }}
          onChange={(value) => {
            if (isNil(value) || (value === '' && treatEmptyAsNil)) {
              onModelChange(null);
            } else {
              onModelChange({
                filterType: 'custom',
                filterKey,
                value,
              });
            }
            api.onFilterChanged();
          }}
          comboboxProps={{
            zIndex: getDefaultZIndex('popover') + 1,
          }}
        />
        {buttons.includes('reset') && (
          <button
            className="ag-button ag-standard-button ag-filter-apply-panel-button"
            onClick={() => {
              onModelChange(null);
              api.onFilterChanged();
            }}
          >
            Reset
          </button>
        )}
      </Stack>
    );
  };
